import { usdCost } from '../../lib/format';
import {
  VOLUME_TICKS,
  ceilingLine,
  estimatePrice,
  freeLine,
  positionToVolume,
  rateText,
  tickLabel,
  useVolume,
  volumeCostUsd,
  volumeNoun,
  volumeToPosition,
} from './costMath';
import type { Endpoint } from '../../features/catalog/model';
import { m } from '../../paraglide/messages';
import { ParaglideMessage } from '@inlang/paraglide-js-react';

/** Slider resolution: one step per 0.1% of the log scale, fine enough that every
 *  two-significant-figure count between the ticks is reachable. */
const STEPS = 1000;

/** "Slider + receipt": a log slider over a request count, the total beside it. */
export const CostEstimate = ({ endpoint }: { endpoint: Endpoint }) => {
  const price = estimatePrice(endpoint);
  const noun = volumeNoun(price);
  const volume = useVolume();
  const free = freeLine(price);
  const ceiling = ceilingLine(price);
  const filled = volumeToPosition(volume.count);
  return (
    <section
      aria-label={m.endpoint_cost_label()}
      className="mt-6 grid gap-6 rounded-12 border border-rule bg-surface p-5 s760:grid-cols-3 s760:p-6"
    >
      <div className="flex flex-col justify-center s760:col-span-2">
        <label className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-16 text-text">
          {m.endpoint_cost_need()}
          <input
            inputMode="numeric"
            value={volume.text}
            onChange={(e) => volume.onText(e.target.value)}
            onBlur={volume.onBlur}
            className="mono w-28 rounded-8 border border-line bg-bg px-2 py-1 text-right text-15 text-text outline-none focus:border-accent-line"
          />
          {noun}
        </label>
        <input
          type="range"
          aria-label={m.endpoint_cost_count_label({ noun })}
          min={0}
          max={STEPS}
          value={Math.round(filled * STEPS)}
          onChange={(e) => volume.setCount(positionToVolume(Number(e.target.value) / STEPS))}
          style={{
            background: `linear-gradient(to right, var(--accent) ${filled * 100}%, var(--line) ${filled * 100}%)`,
          }}
          className="mt-6 h-1 w-full cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent [&::-moz-range-thumb]:bg-surface [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent [&::-webkit-slider-thumb]:bg-surface"
        />
        <div className="relative mt-2 h-4 text-12 text-text-3">
          {VOLUME_TICKS.map((tick, i) => (
            <button
              key={tick}
              type="button"
              onClick={() => volume.setCount(tick)}
              style={{ left: `${volumeToPosition(tick) * 100}%` }}
              className={`mono absolute top-0 hover:text-accent ${
                i === 0 ? '' : i === VOLUME_TICKS.length - 1 ? '-translate-x-full' : '-translate-x-1/2'
              }`}
            >
              {tickLabel(tick)}
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-rule pt-5 s760:border-t-0 s760:border-l s760:pt-0 s760:pl-6">
        <div className="text-13 text-text-3">{m.endpoint_cost_you_pay()}</div>
        <div className="mono mt-1 text-34 leading-tight text-text">
          {usdCost(volumeCostUsd(price, volume.count))}
        </div>
        <div className="mt-2 text-13 text-text-3">
          <ParaglideMessage
            message={m.endpoint_cost_rate_note}
            inputs={{ rate: rateText(price) }}
            markup={{ rate: ({ children }) => <span className="mono text-text-2">{children}</span> }}
          />
        </div>
        {ceiling && <div className="mt-1 text-13 text-text-3">{ceiling}</div>}
        {free && <div className="mt-3 text-13 text-text-2">{free}</div>}
      </div>
    </section>
  );
};
